import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, 
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, Chip, Divider, Alert, TextField, FormControlLabel, Checkbox
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { getProformaStatusColor, getProformaStatusText } from '../../../utils/ProformaStatusUtils';
import { formatMoney } from '../../../utils/currency';

const AcceptProformaModal = ({ open, onClose, proforma, onAccept }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [notifyClient, setNotifyClient] = useState(true);

  if (!proforma) return null;

  const formatCurrency = (amount) => {
    return formatMoney(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY');
  };


  const handleAccept = async () => {
    try {
      setLoading(true);
      await onAccept(proforma, { changeNote: changeNote.trim() || null, notifyClient });
      setChangeNote('');
      onClose();
    } catch (error) {
      console.error('Error accepting proforma:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon('checkmarkDone')}
          {t('accept_proforma')}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('accept_proforma_warning')}
          </Alert>
        </Box>

        {/* Información de la Proforma */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('proforma_information')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('proforma_number')}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {proforma.id}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('client')}
              </Typography>
              <Typography variant="body1">
                {proforma.nombreFantasia || proforma.razonSocial || proforma.clientName || t('unassigned')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('issue_date')}
              </Typography>
              <Typography variant="body1">
                {formatDate(proforma.issueDate)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('branch')}
              </Typography>
              <Typography variant="body1">
                {proforma.branchName || `Sucursal ${proforma.branchId}`}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {t('status')}
              </Typography>
              <Chip 
                label={getProformaStatusText(proforma.status, t)} 
                color={getProformaStatusColor(proforma.status)} 
                size="small" 
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Items de la Proforma */}
        <Typography variant="h6" gutterBottom>
          {t('proforma_items')}
        </Typography>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('product')}</TableCell>
                <TableCell align="center">{t('quantity')}</TableCell>
                <TableCell align="center">Stock actual</TableCell>
                <TableCell align="center">{t('unit_price')}</TableCell>
                <TableCell align="center">{t('subtotal')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proforma.items?.map((item, index) => {
                const finalUnitPrice = item.quantity > 0 ? item.totalPrice / item.quantity : item.unitPrice;
                
                return (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {item.productName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {item.quantity}
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        color={item.currentStock != null && item.currentStock < item.quantity ? 'error.main' : 'text.primary'}
                        sx={{ fontWeight: item.currentStock != null && item.currentStock < item.quantity ? 700 : 400 }}
                      >
                        {item.currentStock != null ? item.currentStock : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {formatCurrency(finalUnitPrice)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1" color="success.main" fontWeight="bold">
                        {formatCurrency(item.totalPrice)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Total */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" color="primary.main">
              {t('total')}: {formatCurrency(proforma.totalAmount)}
            </Typography>
          </Box>
        </Box>

        {/* Notas */}
        {proforma.notes && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('notes')}
            </Typography>
            <Typography variant="body2">
              {proforma.notes}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={notifyClient} onChange={(e) => setNotifyClient(e.target.checked)} />}
            label="Avisar al cliente por email (confirmación del pedido, con los cambios si los hubo)"
          />
          <TextField
            label="Nota para el cliente"
            helperText="Se incluye en el mail y la ve en el portal. Opcional."
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            multiline
            rows={2}
            fullWidth
            sx={{ mt: 1 }}
            inputProps={{ maxLength: 1000 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Información sobre la conversión */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('order_creation_info')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('order_creation_description')}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('cancel')}
        </Button>
        <LoadingButton 
          variant="contained" 
          onClick={handleAccept}
          loading={loading}
          disabled={proforma.status !== 'SENT'}
          startIcon={!loading ? getIcon('checkmarkDone') : null}
          color="success"
        >
          {loading ? t('creating_order') : t('accept_and_create_order')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AcceptProformaModal;
